'use client';

import {
  ReleaseNoteViewer,
  ReleaseNoteViewers as ReleaseNoteViewersData,
} from 'bff';
import { format, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslations } from 'i11n';
import { useCallback } from 'react';
import {
  cn,
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from 'ui';
import { buildUserFullName, getInitialsFromUser } from 'utils';

type AvatarSize = 'sm' | 'md' | 'list';

const MS_PER_HOUR = 3_600_000;

// Solid pastel pairs, so overlapping faces stay apart; picked by user id.
const AVATAR_COLORS = [
  'bg-orange-100 text-orange-800',
  'bg-blue-100 text-blue-800',
  'bg-green-100 text-green-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-pink-100 text-pink-800',
  'bg-sky-100 text-sky-800',
  'bg-lime-100 text-lime-800',
];

const READER_COLOR = 'bg-primary text-primary-foreground';

const avatarSizes: Record<AvatarSize, { box: string; reader: string }> = {
  sm: { box: 'h-6 w-6 text-[0.5625rem]', reader: 'text-[0.5rem]' },
  md: {
    box: 'h-[1.875rem] w-[1.875rem] text-[0.6875rem]',
    reader: 'text-[0.5625rem]',
  },
  list: {
    box: 'h-[1.625rem] w-[1.625rem] text-[0.625rem]',
    reader: 'text-[0.5rem]',
  },
};

// Enough overlap to read as a stack, not so much it hides an initial.
const stackOverlap: Record<Exclude<AvatarSize, 'list'>, string> = {
  sm: '[&>*:not(:first-child)]:-ml-[5px]',
  md: '[&>*:not(:first-child)]:-ml-1.5',
};

const hashOf = (text: string) =>
  Array.from(text).reduce(
    (hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0,
    0,
  );

const colorOf = (viewer: ReleaseNoteViewer) =>
  viewer.is_reader
    ? READER_COLOR
    : AVATAR_COLORS[hashOf(viewer.id) % AVATAR_COLORS.length];

const ViewerAvatar = ({
  viewer,
  size,
}: {
  viewer: ReleaseNoteViewer;
  size: AvatarSize;
}) => {
  const t = useTranslations();

  return (
    <span
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-full border-2 border-background font-bold leading-none',
        avatarSizes[size].box,
        viewer.is_reader && avatarSizes[size].reader,
        colorOf(viewer),
      )}
    >
      {viewer.is_reader
        ? t('release-notes.viewers.you')
        : getInitialsFromUser(viewer)}
    </span>
  );
};

// "hace un momento", "hoy 14:32" or "3 nov 14:32".
const useViewedAtLabel = () => {
  const t = useTranslations();

  return useCallback(
    (isoDate: string) => {
      const date = new Date(isoDate);

      if (Date.now() - date.getTime() < MS_PER_HOUR)
        return t('release-notes.viewers.just-now');

      if (isToday(date))
        return t('release-notes.viewers.today', {
          time: format(date, 'HH:mm'),
        });

      return format(date, 'd MMM HH:mm', { locale: es });
    },
    [t],
  );
};

// Who in the company already opened the note: the latest few faces, how many
// in all, and, on hover or focus, who they are and when they opened it. The
// list opens in a portal, so a dialog or a card around it cannot clip it.
export const ReleaseNoteViewers = ({
  viewers,
  max = 5,
  size = 'md',
  withLabel = true,
  className,
}: {
  viewers: ReleaseNoteViewersData;
  // Faces in the stack; the rest is a "+N".
  max?: number;
  size?: Exclude<AvatarSize, 'list'>;
  withLabel?: boolean;
  className?: string;
}) => {
  const t = useTranslations();
  const viewedAtLabel = useViewedAtLabel();

  if (!viewers.count) return null;

  const shown = viewers.sample.slice(0, max);
  const rest = viewers.count - shown.length;
  const unlisted = viewers.count - viewers.sample.length;
  const countLabel =
    viewers.count === 1
      ? t('release-notes.viewers.one')
      : t('release-notes.viewers.many', { count: viewers.count });

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <div
          tabIndex={0}
          aria-label={t('release-notes.viewers.aria', {
            count: viewers.count,
          })}
          className={cn(
            'inline-flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <div className={cn('flex', stackOverlap[size])}>
            {shown.map((viewer) => (
              <ViewerAvatar key={viewer.id} viewer={viewer} size={size} />
            ))}
            {rest > 0 && (
              <span
                className={cn(
                  'inline-grid min-w-fit shrink-0 place-items-center rounded-full border-2 border-background bg-stone-200 px-1.5 font-bold leading-none text-stone-700',
                  avatarSizes[size].box,
                  'w-auto',
                )}
              >
                +{rest}
              </span>
            )}
          </div>
          {withLabel && (
            <span className='whitespace-nowrap text-xs text-muted-foreground'>
              {countLabel}
            </span>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent align='end' className='w-[17rem] p-3 text-left'>
          <p className='mb-1.5 text-xs font-semibold text-muted-foreground'>
            {t('release-notes.viewers.title')}
          </p>
          <ul className='flex flex-col'>
            {viewers.sample.map((viewer) => (
              <li key={viewer.id} className='flex items-center gap-2 py-1.5'>
                <ViewerAvatar viewer={viewer} size='list' />
                <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                  <span className='truncate text-[0.8125rem] text-foreground'>
                    {viewer.is_reader
                      ? t('release-notes.viewers.you')
                      : buildUserFullName(viewer)}
                  </span>
                  {(viewer.is_reader || viewer.role) && (
                    <small className='truncate text-[0.6875rem] text-muted-foreground'>
                      {viewer.is_reader
                        ? t('release-notes.viewers.your-user')
                        : viewer.role}
                    </small>
                  )}
                </span>
                <time
                  dateTime={viewer.viewed_at}
                  className='whitespace-nowrap text-[0.6875rem] text-muted-foreground'
                >
                  {viewedAtLabel(viewer.viewed_at)}
                </time>
              </li>
            ))}
          </ul>
          {unlisted > 0 && (
            <p className='mt-1.5 text-xs text-muted-foreground'>
              {unlisted === 1
                ? t('release-notes.viewers.more-one')
                : t('release-notes.viewers.more', { count: unlisted })}
            </p>
          )}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
